import type { MapEngine } from '@mighty-map/map-engine';
import { createDataApi } from '../../api/dataApi';
import { changedLayers, isStale, parseLayerEvent } from '../../lib/manifest';
import { connectSse } from '../../lib/sse';
import { useMapStore } from '../../state/mapStore';

/**
 * Loads the channel manifest, then listens for publishes. On an event whose hash the manifest lacks, it re-fetches
 * the manifest and hot-switches each changed layer's source on the live engine. No reload, no remount.
 */
export function startLiveUpdates(opts: {
  dataApiUrl: string;
  channel: string;
  getToken: () => Promise<string>;
  getEngine: () => MapEngine | null;
  onError?: (message: string) => void;
}): () => void {
  const api = createDataApi(opts.dataApiUrl, opts.getToken);
  const store = useMapStore;
  let refreshing: Promise<void> | null = null;
  let again = false;
  let closed = false;

  const refresh = async (): Promise<void> => {
    const prev = store.getState().manifest;
    const next = await api.getManifest(opts.channel);
    if (closed) return;
    const engine = opts.getEngine();
    const changed = prev ? changedLayers(prev, next) : [];
    if (engine) for (const l of changed) engine.swapSource(l.layer_id, l.tiles_url);
    store.getState().setManifest(next);
    if (prev && changed.length > 0) store.getState().markDataUpdated(new Date());
  };

  // Serialise refreshes; a burst of events collapses into at most one follow-up fetch.
  const scheduleRefresh = (): void => {
    if (refreshing) {
      again = true;
      return;
    }
    refreshing = refresh()
      .catch((e: unknown) => opts.onError?.(e instanceof Error ? e.message : String(e)))
      .finally(() => {
        refreshing = null;
        if (again && !closed) {
          again = false;
          scheduleRefresh();
        }
      });
  };

  scheduleRefresh();
  const stream = connectSse({
    url: api.eventsUrl(opts.channel),
    getToken: opts.getToken,
    onMessage: (msg) => {
      const event = parseLayerEvent(msg.data);
      if (event && isStale(store.getState().manifest, event)) scheduleRefresh();
    },
  });

  return () => {
    closed = true;
    stream.close();
  };
}
